// ./src/utils/collectionHelpers.ts
//
// Small, dependency-free helpers for updating in-memory lists by a key (default
// "id"). These are the reusable building blocks every entity hook shares — no
// abstraction, just pure functions that return a new array.

/** Any object we can key by one of its own fields. */
type Keyed = Record<string, any>;

/**
 * Insert or update `item` in `list`, matched by `key`. When the item already
 * exists it's replaced in place; otherwise it's added — to the front by default
 * (newest-first lists), or the end with `at: "end"`.
 */
export const upsertBy = <T extends Keyed>(
  list: T[],
  item: T,
  key: keyof T = "id" as keyof T,
  at: "start" | "end" = "start"
): T[] => {
  const index = list.findIndex((i) => i[key] === item[key]);

  if (index >= 0) {
    const next = [...list];
    next[index] = item;
    return next;
  }

  return at === "start" ? [item, ...list] : [...list, item];
};

/** Upsert several items by `key`, preserving their relative order. */
export const upsertManyBy = <T extends Keyed>(
  list: T[],
  items: T[],
  key: keyof T = "id" as keyof T,
  at: "start" | "end" = "start"
): T[] => items.reduce((acc, item) => upsertBy(acc, item, key, at), list);

/** Remove the item whose `key` equals `id`. */
export const removeBy = <T extends Keyed>(
  list: T[],
  id: unknown,
  key: keyof T = "id" as keyof T
): T[] => list.filter((i) => i[key] !== id);

/** Remove every item whose `key` is in `ids`. */
export const removeManyBy = <T extends Keyed>(
  list: T[],
  ids: unknown[],
  key: keyof T = "id" as keyof T
): T[] => {
  const set = new Set(ids);
  return list.filter((i) => !set.has(i[key]));
};
